import { AccountService } from './accountService';
import { GamesService } from './gamesService';
import { DatabaseService } from './databaseService';
import { UserService } from './userService';
import { Service, ServiceResponse } from './service';
import { ShopService } from './shopService';
import { PublicService } from './publicService';
import { MessagesService } from './messagesService';
import { NetworkService } from './networkService';

export class Server {
  public static account: AccountService = new AccountService();
  public static database: DatabaseService = new DatabaseService();
  public static user: UserService = new UserService();
  public static games: GamesService = new GamesService();
  public static shop: ShopService = new ShopService();
  public static public: PublicService = new PublicService();
  public static messages: MessagesService = new MessagesService();
  public static network: NetworkService = new NetworkService();
  public static initialized: boolean = false;
  
  protected static signUpDetails: any = null;

  public static async init(environment:string = 'dev') {
    Service.setEnvironment(environment);

    let promises = [];
    promises.push(Server.database.init());
    promises.push(Server.user.init());
    promises.push(Server.games.init());
    promises.push(Server.shop.init());
    promises.push(Server.messages.init());

    let responses = await Promise.all(promises);

    for(let i = 0; i < responses.length; i++)
      if(!responses[i].success)
        return responses[i];

    await this.autoLogin();
    
    Server.initialized = true;
  }

  public static getEnvironment() {
    return Service.getEnvironment();
  }

  public static async startSignUp(email: string, password: string): Promise<ServiceResponse> {
    let response = await Server.account.signUp(email, password);
    this.signUpDetails = response.success ? {email, password} : null;
    return response;
  }

  public static async completeSignUp(code: string): Promise<ServiceResponse> {
    let response = await Server.account.confirmRegistration(code);
    if(response.success)
      await this.login(this.signUpDetails.email, this.signUpDetails.password);
    return response;
  }

  public static async login(email: string, password: string): Promise<ServiceResponse> {
    let response = await Server.account.authenticate(email, password);
    if(!response.success)
      return response;

    let refreshToken = Server.account.getRefreshToken();
    window.localStorage.setItem('RefreshToken', refreshToken);
    window.localStorage.setItem('RefreshEmail', email);

    return Server.finishLogin();
  }

  public static async autoLogin(): Promise<ServiceResponse> {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');

    if(!email || !token)
      return {success: false};

    let response = await Server.account.authenticateWithToken(email, token);
    if(!response.success)
      return response;

    return Server.finishLogin();
  }

  protected static async finishLogin() {
    Service.setAuthorizationToken(Server.account.getAuthorizationToken());

    let response = await Server.sync();
    if(!response.success)
      return response;

    await Server.network.connect();
  
    // await this.user.setPresence('site');
    Server.account.notifyListeners('login');

    return {success: true};
  }

  public static async signOut() {
    await Server.account.signOut();
    Server.user.clear();
    Server.network.disconnect();
    window.localStorage.removeItem('RefreshToken');
    window.localStorage.removeItem('RefreshEmail');
    Server.account.notifyListeners('logout');
  }

  public static async sync(): Promise<ServiceResponse> {
    let promises = [];
    promises.push(Server.user.sync());
    promises.push(Server.shop.sync());

    let responses = await Promise.all(promises);

    for(let i = 0; i < responses.length; i++)
      if(!responses[i].success)
        return responses[i];

    await Server.messages.sync();  // must do after user is done

    return {success: true};
  }

  public static async refreshServiceLogin(): Promise<ServiceResponse> {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');

    if(email && token) {
      let response = await Server.account.authenticateWithToken(email, token);
      if(!response.success)
        return response;

      console.log('Authentication token refreshed.');
        
      Service.setAuthorizationToken(Server.account.getAuthorizationToken());

      return response;
    }

    return {success: false};
  }
}
